import { useEffect, useRef, useState } from "preact/hooks";
import type { FeedbackListItem, WidgetApiConfig } from "../api/client";
import { getOrCreateClientId } from "../api/client-id";
import { mediaUrl } from "../api/client";
import { activeTeamCode, readProfile } from "../api/profile";
import { ArrowLeftIcon } from "./icons";

type Props = {
  item: FeedbackListItem;
  apiConfig: WidgetApiConfig;
  onBack: () => void;
};

type SidebarTab = "info" | "pins";

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_TONES: Record<string, string> = {
  new: "mf-status-new",
  in_progress: "mf-status-progress",
  resolved: "mf-status-resolved",
  closed: "mf-status-closed",
};

function relTime(at: number | string): string {
  const now = Date.now();
  const diff = Math.max(0, now - new Date(at).getTime());
  const m = Math.round(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function capitalize(s: string) {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

export function FeedbackDetailView({ item, apiConfig, onBack }: Props) {
  const [mediaLoading, setMediaLoading] = useState(false);
  const [activePinIdx, setActivePinIdx] = useState<number | null>(null);
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("info");
  const overlayRef = useRef<HTMLDivElement>(null);

  const hasMedia = item.hasScreenshot || item.hasVideo;
  // The widget bundle and the API deploy independently, so a browser can be
  // running a build newer than the server. Tolerate an item without `pins`
  // rather than crashing the whole detail view during that skew window.
  const pins = item.pins ?? [];

  // URL-nya dihitung, bukan diminta: item sudah membawa daftar medianya, jadi
  // satu panggilan jaringan lagi cuma untuk menyusun string tidak ada gunanya.
  // Elemen <img>/<video> yang menentukan kapan bytenya benar-benar ditarik.
  const first = item.media?.[0] ?? null;
  const media = first
    ? {
        url: mediaUrl(apiConfig, first.id, { browserId: getOrCreateClientId(), teamCode: activeTeamCode(readProfile()) }),
        type: (first.kind === "video" ? "video" : "screenshot") as "video" | "screenshot",
        contentType: first.mime,
      }
    : null;

  // Close pin popover on outside click — must use composedPath() because in
  // Shadow DOM e.target is retargeted to the shadow host, making closest()
  // fail even when clicking inside the overlay.
  useEffect(() => {
    if (activePinIdx === null) return;
    function onPointerDown(e: PointerEvent) {
      const path = e.composedPath?.() ?? [];
      const insidePin = path.some(
        (node) =>
          node instanceof Element &&
          (node.hasAttribute("data-pin-popup") || node.hasAttribute("data-pin-btn")),
      );
      if (!insidePin) setActivePinIdx(null);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [activePinIdx]);

  const reporter = item.reporter?.name ?? item.reporter?.email ?? null;

  return (
    <div class="mf-detail-overlay" ref={overlayRef}>
      {/* ── Header ── */}
      <div class="mf-detail-header">
        <button type="button" class="mf-detail-back" onClick={onBack} aria-label="Back">
          <ArrowLeftIcon />
          <span>Back</span>
        </button>
        <div class="mf-detail-header-right">
          <span class={`mf-status-pill ${STATUS_TONES[item.status] ?? ""}`}>
            {STATUS_LABELS[item.status] ?? item.status}
          </span>
          <span class="mf-detail-time">{relTime(item.createdAt)}</span>
        </div>
      </div>

      {/* ── Main: screenshot + sidebar ── */}
      <div class="mf-detail-main">
        {/* Left: screenshot or video */}
        <div class="mf-detail-canvas">
          {!hasMedia ? (
            <div class="mf-detail-img-placeholder">
              <span style={{ fontSize: 12, color: "#6b7280" }}>No media attached</span>
            </div>
          ) : mediaLoading ? (
            <div class="mf-detail-img-placeholder"><div class="mf-spinner" /></div>
          ) : !media ? (
            <div class="mf-detail-img-placeholder">
              <span style={{ fontSize: 12, color: "#6b7280" }}>Media unavailable</span>
            </div>
          ) : media.type === "video" ? (
            /* Video player */
            <div class="mf-detail-video-wrap">
              <video
                class="mf-detail-video"
                src={media.url}
                controls
                autoplay={false}
                playsInline
                preload="metadata"
              />
            </div>
          ) : (
            /* Screenshot + pins */
            <div class="mf-detail-img-wrap">
              <img
                src={media.url}
                alt="Screenshot"
                class="mf-detail-img"
                draggable={false}
              />
              {pins.map((pin, idx) => {
                const isActive = activePinIdx === idx;
                const flipLeft = pin.xPct > 60;
                const flipUp = pin.yPct > 70;
                return (
                  <div
                    key={idx}
                    class="mf-detail-pin-wrap"
                    style={{ left: `${pin.xPct}%`, top: `${pin.yPct}%` }}
                  >
                    <button
                      type="button"
                      data-pin-btn=""
                      class={`mf-detail-pin-btn ${isActive ? "is-active" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActivePinIdx(isActive ? null : idx);
                      }}
                      aria-label={`Pin ${idx + 1}`}
                    >
                      {idx + 1}
                    </button>
                    {isActive && (
                      <div
                        data-pin-popup=""
                        class={`mf-detail-pin-popup ${flipLeft ? "is-left" : "is-right"} ${flipUp ? "is-up" : "is-down"}`}
                      >
                        <div class="mf-detail-pin-label">Pin {idx + 1}</div>
                        {pin.note ? (
                          <p class="mf-detail-pin-note">{pin.note}</p>
                        ) : (
                          <p class="mf-detail-pin-note mf-detail-pin-note-empty">No note.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: sidebar */}
        <div class="mf-detail-sidebar">
          <div class="mf-detail-sidebar-title">{item.title}</div>
          {item.url && <div class="mf-detail-sidebar-url">{shortUrl(item.url)}</div>}

          {/* Sidebar tabs */}
          <div class="mf-detail-sidebar-tabs">
            <button
              type="button"
              class={`mf-detail-sidebar-tab ${sidebarTab === "info" ? "is-active" : ""}`}
              onClick={() => setSidebarTab("info")}
            >
              Info
            </button>
            {pins.length > 0 && (
              <button
                type="button"
                class={`mf-detail-sidebar-tab ${sidebarTab === "pins" ? "is-active" : ""}`}
                onClick={() => setSidebarTab("pins")}
              >
                Pins ({pins.length})
              </button>
            )}
          </div>

          <div class="mf-detail-sidebar-body">
            {sidebarTab === "info" && (
              <div class="mf-detail-info-list">
                <Row label="Type" value={capitalize(item.type)} />
                <Row label="Severity" value={capitalize(item.severity)} />
                <Row label="Status" value={STATUS_LABELS[item.status] ?? item.status} />
                {reporter && <Row label="Reporter" value={reporter} />}
                {item.description && (
                  <div class="mf-detail-info-row mf-detail-info-desc">
                    <div class="mf-detail-info-label">Description</div>
                    <p class="mf-detail-info-value mf-detail-desc-text">{item.description}</p>
                  </div>
                )}
                <div class="mf-detail-info-row">
                  <div class="mf-detail-info-label">Submitted</div>
                  <div class="mf-detail-info-value">{relTime(item.createdAt)}</div>
                </div>
              </div>
            )}

            {sidebarTab === "pins" && (
              <ul class="mf-detail-pins-list">
                {pins.map((pin, idx) => (
                  <li
                    key={idx}
                    class={`mf-detail-pins-item ${activePinIdx === idx ? "is-active" : ""}`}
                    onClick={() => setActivePinIdx(activePinIdx === idx ? null : idx)}
                  >
                    <span class="mf-detail-pin-num">{idx + 1}</span>
                    <span class="mf-detail-pin-text">
                      {pin.note || <em class="mf-detail-pin-no-note">No note</em>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div class="mf-detail-info-row">
      <div class="mf-detail-info-label">{label}</div>
      <div class="mf-detail-info-value">{value}</div>
    </div>
  );
}

function shortUrl(u: string): string {
  try {
    const p = new URL(u);
    const path = p.pathname.length > 35 ? `${p.pathname.slice(0, 32)}…` : p.pathname;
    return `${p.host}${path}`;
  } catch {
    return u.length > 50 ? `${u.slice(0, 47)}…` : u;
  }
}
